'use client';

import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIncidentsFilters } from '../../hooks/useIncidentsFilters';

interface IncidentsFiltersPanelProps {
  className?: string;
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** IDs de tienda/courier/equipo sincronizados con query params (fuera de filterConditions) */
  shopIds?: string[];
  teamIds?: string[];
  courierIds?: string[];
  onShopChange?: (ids: string[]) => void;
  onTeamChange?: (ids: string[]) => void;
  onCourierChange?: (ids: string[]) => void;
  onClearAll?: () => void;
}

export const IncidentsFiltersPanel = ({
  className,
  initialConditions = [],
  onChange,
  shopIds = [],
  teamIds = [],
  courierIds = [],
  onShopChange,
  onTeamChange,
  onCourierChange,
  onClearAll,
}: IncidentsFiltersPanelProps) => {
  const { t } = useTranslation('incidentsModule');

  const {
    filters,
    is3pl,
    courierOptions,
    teamOptions,
    isLoadingCouriers,
    isLoadingTeams,
    loadShopsForMultiSelect,
    handleStatusChange,
    handleReportDateChange,
    handleResolutionDateChange,
    handleExternalCodeChange,
    clearAllFilters,
  } = useIncidentsFilters({ initialConditions, onChange });

  const [localExternalCode, setLocalExternalCode] = useState(filters.external_code);

  useEffect(() => {
    setLocalExternalCode((prev) => (prev === filters.external_code ? prev : filters.external_code));
  }, [filters.external_code]);

  const statusOptions = useMemo(
    () => [
      { value: 'pending', label: t('table.statuses.pending') },
      { value: 'in_progress', label: t('table.statuses.in_progress') },
      { value: 'resolved', label: t('table.statuses.resolved') },
      { value: 'canceled', label: t('table.statuses.canceled') },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.external_code?.trim()) count++;
    if (filters.status.length > 0) count++;
    if (shopIds.length > 0) count++;
    if (teamIds.length > 0) count++;
    if (courierIds.length > 0) count++;
    if (filters.report_date?.from && filters.report_date?.to) count++;
    if (filters.resolution_date?.from && filters.resolution_date?.to) count++;
    return count;
  }, [filters, shopIds, teamIds, courierIds]);

  const handleClearAll = () => {
    setLocalExternalCode('');
    clearAllFilters();
    onShopChange?.([]);
    onTeamChange?.([]);
    onCourierChange?.([]);
    onClearAll?.();
  };

  const applyExternalCode = () => {
    handleExternalCodeChange(localExternalCode.trim());
  };

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.external_code')}>
            <input
              type="text"
              value={localExternalCode}
              onChange={(e) => setLocalExternalCode(e.target.value)}
              onBlur={applyExternalCode}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyExternalCode();
              }}
              placeholder={t('filters.search_placeholder')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-external-code"
            />
          </FilterField>

          <FilterField label={t('filters.status')}>
            <MultiSelect
              options={statusOptions}
              value={filters.status}
              onValueChange={handleStatusChange}
              placeholder={t('filters.select_status', 'Todos los estados')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-status"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.shop', 'Tienda')}>
            <MultiSelect
              loadOptions={loadShopsForMultiSelect}
              value={shopIds}
              onValueChange={onShopChange}
              placeholder={t('filters.all_shops', 'Todas las tiendas')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-shop"
              searchPlaceholder={t('filters.search_shops', 'Buscar tiendas...')}
              triggerClassName="h-[38px]"
              pageSize={20}
            />
          </FilterField>

          <FilterField label={t('filters.courier', 'Courier')}>
            <MultiSelect
              options={courierOptions}
              value={courierIds}
              onValueChange={onCourierChange}
              placeholder={
                isLoadingCouriers
                  ? t('filters.loading', 'Cargando...')
                  : t('filters.all_couriers', 'Todos los couriers')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCouriers}
              dataTestId="filter-courier"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          {is3pl && (
            <FilterField label={t('filters.team', 'Equipo')}>
              <MultiSelect
                options={teamOptions}
                value={teamIds}
                onValueChange={onTeamChange}
                placeholder={
                  isLoadingTeams ? t('filters.loading', 'Cargando...') : t('filters.all_teams', 'Todos los equipos')
                }
                emptyText={t('filters.notFound', 'Sin resultados')}
                disabled={isLoadingTeams}
                dataTestId="filter-team"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          )}
        </div>
      </FilterSection>

      <FilterSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.report_date')}>
            <DateRangePicker
              initialDateFrom={filters.report_date?.from}
              initialDateTo={filters.report_date?.to}
              onUpdate={({ range }) => handleReportDateChange(range)}
              placeholder={t('filters.select_placeholder', 'Seleccionar...')}
              dataTestId="filter-report-date"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.resolution_date')}>
            <DateRangePicker
              initialDateFrom={filters.resolution_date?.from}
              initialDateTo={filters.resolution_date?.to}
              onUpdate={({ range }) => handleResolutionDateChange(range)}
              placeholder={t('filters.select_placeholder', 'Seleccionar...')}
              dataTestId="filter-resolution-date"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={handleClearAll}
            type="button"
          >
            {t('filters.clear_count', {
              count: activeFiltersCount,
              defaultValue: `Limpiar {{count}} ${activeFiltersCount === 1 ? 'filtro' : 'filtros'}`,
            })}
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ children }: { children: React.ReactNode }) => <div className="space-y-3">{children}</div>;

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </div>
);
